import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import utils from '../../../lib/utils';
import {
  getOrderDetail,
  activatePortInOrder,
  cancelPendingOrder,
  getOrderDocument,
} from '../../../actions/Inteliquent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  UncontrolledTooltip,
  Input,
} from 'reactstrap';
import Table from '../../../components/Table';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { groupBy } from 'lodash';

const status = {
  success: <span className="rounded-circle bg-success icon" />,
  warning: <span className="rounded-circle bg-warning icon" />,
  danger: <span className="rounded-circle bg-danger icon" />,
  secondary: <span className="rounded-circle bg-secondary icon" />,
};

export default function PortInOrderDetail() {
  const token = localStorage.getItem('jwtToken') || null;
  const history = useHistory();
  const dispatch = useDispatch();
  const pram = useParams();
  const [checkedGroups, setCheckedGroups] = useState([]);
  const [data, setData] = useState();
  const [activeModel, setActiveModel] = useState('');

  useEffect(() => {
    if (!data) {
      const data = dispatch(getOrderDetail(token, pram.id));
      const document = dispatch(getOrderDocument(token, pram.id));
      Promise.all([data, document])
        .then(([{ orderDetailResponse }, { orderDocumentList: { orderDocument } }]) =>
          setData({ ...orderDetailResponse, orderDocument }),
        )
        .catch(console.error);
    }
  }, [data]);
  const column = useMemo(
    () => [
      {
        dataIndex: 'tn',
        label: 'Telephone Number',
      },
      {
        dataIndex: 'tnStatus',
        label: 'Status',
      },
      {
        dataIndex: 'portDate',
        label: 'Port Date',
        render: value => value ?? '-',
      },
      {
        dataIndex: 'portTime',
        label: 'Port Time',
        render: value => value ?? '-',
      },
      {
        dataIndex: 'trunkGroup',
        label: 'Trunk Group',
      },
      {
        dataIndex: 'rateCenter',
        label: 'Rate Center',
      },
      {
        dataIndex: 'endUser',
        key: 'state',
        label: 'State',
        render: value => value.state ?? '-',
      },
      {
        dataIndex: 'cnamDip',
        label: 'CNAME DIP',
        render: value =>
          value === 'Y' ? status.success : value === 'N' ? status.danger : status.secondary,
      },
      {
        dataIndex: 'cnamEnabled',
        label: 'CNAME',
        render: value =>
          value === 'Y' ? status.success : value === 'N' ? status.danger : status.secondary,
      },
      {
        dataIndex: 'dlEnabled',
        label: 'Direct Listing',
        render: value =>
          value === 'Y' ? status.success : value === 'N' ? status.danger : status.secondary,
      },
      {
        dataIndex: 'e911Enabled',
        label: 'E911',
        render: value =>
          value === 'Y' ? status.success : value === 'N' ? status.danger : status.secondary,
      },
      {
        dataIndex: 'msgEnabled',
        label: 'Messaging',
        render: value =>
          value === 'Y' ? status.success : value === 'N' ? status.danger : status.secondary,
      },
    ],
    [data?.tnList],
  );

  if (!data) return <div className="lead">Loading</div>;

  let groups = groupBy(data.tnList.tnItem, tn => tn.tnGroup);
  function onActivatePortingOrder() {
    setActiveModel('')
    const loading = toast.info('Activating order');
    dispatch(
      activatePortInOrder(token, {
        orderId: data.orderId,
        customerOrderReference: data.customerOrderReference,
        tnGroup: checkedGroups,
      }),
    )
      .then(response => {
        if (response.status.includes("Success")) {
          dispatch({
            type: 'NUMBER_ACTIVATED_ON_PORTING_TAB',
            payload: JSON.parse(JSON.stringify(response.status)),
          });
          history.push(`/provisioning/porting/order/${pram.id}/activate`);
        } else {
          toast.error(response.status)
        }
      })
      .catch(() => toast.error('Order Activation Failed'))
      .finally(() => toast.dismiss(loading));
  }

  function onCancelPortingOrder() {
    if (checkedGroups.length < 1) return toast.error('Please select the groups!');
    const loading = toast.info('Canceling order');
    dispatch(
      cancelPendingOrder(token, {
        orderId: data.orderId,
        customerOrderReference: data.customerOrderReference,
      }),
    )
      .then(() => {
        toast.success('Order Canceled successfull');
        setActiveModel('');
      })
      .catch(() => toast.error('Order Cancellation Failed'))
      .finally(() => toast.dismiss(loading));
  }

  const toggleGroup = (checked, group) => {
    setCheckedGroups(arr => {
      if (checked) return [...arr, group];
      else return arr.filter(key => key != group);
    });
  };
  const selectAllGroups = checked => {
    setCheckedGroups(arr => {
      if (checked) return Object.keys(groups);
      else return [];
    });
  };
  const checkButtonStatus =
    data.orderStatus !== 'Pending Cancel' &&
    data.orderStatus !== 'Canceled' &&
    data.orderStatus !== 'Closed';

  const checkButtonToolTip =
    data.orderStatus === 'Pending Cancel' ||
    data.orderStatus === 'Canceled' ||
    data.orderStatus === 'Closed';
  const isAllGroupChecked = () => checkedGroups.length === Object.keys(groups).length;
  return (
    <>
      <div>
        <div className="d-flex justify-content-between">
          <div className="h3 font-weight-bold">Port In Order Detail</div>

          <div className="d-flex justify-content-between">
            <div className="text-right">
              <div className="btn-group">
                <Button
                  id="activeorderTooltip"
                  onClick={() => {
                    if (checkedGroups.length < 1) return toast.error('Please select the groups!');
                    if (checkButtonStatus) setActiveModel('activeNumber');
                  }}
                  color="info"
                >
                  Activate Numbers
                </Button>
                {checkButtonToolTip && (
                  <UncontrolledTooltip target="activeorderTooltip">
                    Order is {data.orderStatus}
                  </UncontrolledTooltip>
                )}
                <Button
                  id="editorderTooltip"
                  onClick={() => {
                    if (checkButtonStatus)
                      history.push(`/provisioning/porting/create-order/${data.orderId}?&step=3`);
                  }}
                  color="info"
                >
                  Edit Order Info
                </Button>
                {checkButtonToolTip && (
                  <UncontrolledTooltip target="editorderTooltip">
                    Order is {data.orderStatus}
                  </UncontrolledTooltip>
                )}
                <Button
                  id="editnumberTooltip"
                  onClick={() => {
                    if (checkedGroups.length < 1) return toast.error('Please select the groups!');
                    if (checkButtonStatus) {
                      const temp = checkedGroups.map(group => encodeURIComponent(group))
                      history.push(
                        `/provisioning/porting/create-order/${data.orderId}?&step=1&groups=${temp.join(',')}`,
                      );
                    }
                  }}
                  color="info"
                >
                  Edit Number Info
                </Button>
                {checkButtonToolTip && (
                  <UncontrolledTooltip target="editnumberTooltip">
                    Number is {data.orderStatus}
                  </UncontrolledTooltip>
                )}
                <Button
                  id="cancelOrderTooltip"
                  onClick={() => {
                    if (checkButtonStatus) setActiveModel('cancelOrder');
                  }}
                  color="danger"
                >
                  Cancel Order
                </Button>
                {checkButtonToolTip && (
                  <UncontrolledTooltip target="cancelOrderTooltip">
                    Order is already {data.orderStatus}
                  </UncontrolledTooltip>
                )}
              </div>
            </div>
          </div>
        </div>

        <Row>
          <Col sm={24} md={4}>
            <span className="text-primary">PON : </span> {data.customerOrderReference ?? '-'}
          </Col>
          <Col sm={24} md={4}>
            <span className="text-primary">Order Status : </span> {data.orderStatus ?? '-'}
          </Col>
          <Col sm={24} md={4}>
            <span className="text-primary">Desired Due Date : </span>
            {data.desiredDueDate ? utils.getDateFormate(data.desiredDueDate) : '-'}
          </Col>
          <Col sm={24} md={4}>
            <span className="text-primary">Order ID : </span> {data.orderId ?? '-'}
          </Col>
          <Col sm={24} md={4}>
            <span className="text-primary">Order Created By : </span> {data.createdUser ?? '-'}
          </Col>
          <Col sm={24} md={4}>
            <span className="text-primary">Reseller Name : </span> {data.resellerName ?? '-'}
          </Col>
        </Row>

        <div>
          <div className="h5 font-weight-bold w-100 pt-3 text-primary">Instruction</div>
          <div>In the table below</div>
          <div>
            <div>{status.success} = Accepted or Complete</div>
            <div>{status.warning} = Reciveved and processing</div>
            <div>{status.danger} = Rejected or Failed</div>
            <div>{status.secondary} = Not Applicable</div>
          </div>
          <div>More precise information can be obtained by hovering over the dot</div>
        </div>
        <div className="h6 w-100 pt-3">
          &nbsp;&nbsp;
          <Input
            type="checkbox"
            checked={isAllGroupChecked()}
            onChange={evt => selectAllGroups(evt.target.checked)}
          />
          &nbsp;&nbsp;&nbsp; Select All
        </div>
        {Object.entries(groups).map(([group, value]) => {
          return (
            <div key={group}>
              <div className="h5 font-weight-bold w-100 pt-3 text-primary">
                &nbsp;&nbsp;
                <Input
                  type="checkbox"
                  checked={checkedGroups.includes(group)}
                  onChange={evt => toggleGroup(evt.target.checked, group)}
                />
                &nbsp;&nbsp;&nbsp; Current Carrier # {group}
              </div>

              <div className="mt-3">
                <Table data={value} column={column} />
              </div>
            </div>
          );
        })}
      </div>

      {data.orderNotes && (
        <div>
          <div className="h5 font-weight-bold w-100 pt-3 text-primary">Notes</div>
          {data.orderNotes.map(({ insertDate, insertUser, note }, index) => (
            <Row key={index} className="my-3">
              <Col sm={24} md={4}>
                <div className="text-primary">Note</div>
                <div>{note}</div>
              </Col>
              <Col sm={24} md={4}>
                <div className="text-primary">Created Date</div>
                <div>{insertDate}</div>
              </Col>
              <Col sm={24} md={4}>
                <div className="text-primary">Entered By</div>
                <div>{insertUser}</div>
              </Col>
            </Row>
          ))}
        </div>
      )}

      <div>
        <div className="h5 font-weight-bold w-100 pt-3 text-primary">Attachment</div>
        <div>
          {data.orderDocument && data.orderDocument.length ? (
            data.orderDocument.map(({ documentName, documentId, fileContent }) => (
              <a
                key={documentId}
                download={documentName}
                href={'data:application/octet-stream;base64,' + fileContent}
                className="btn btn-primary mr-2 mb-2"
              >
                <FontAwesomeIcon icon="download" size="sm" className="mr-2" />
                {documentName}
              </a>
            ))
          ) : (
            <div>No Attachment</div>
          )}
        </div>
      </div>

      <Modal isOpen={activeModel === 'activeNumber'} toggle={() => setActiveModel('')}>
        <ModalHeader toggle={() => setActiveModel('')}>Activate Numbers</ModalHeader>
        <ModalBody>You sure you want to activate these numbers</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={onActivatePortingOrder}>
            Yes
          </Button>
          <Button color="secondary" onClick={() => setActiveModel('')}>
            No
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={activeModel === 'cancelOrder'} toggle={() => setActiveModel('')}>
        <ModalHeader toggle={() => setActiveModel('')}>Cancel Order</ModalHeader>
        <ModalBody>You sure you want to cancel these numbers</ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={onCancelPortingOrder}>
            Yes
          </Button>
          <Button color="secondary" onClick={() => setActiveModel('')}>
            No
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
